import { Link, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CallButton } from '../../components/CallButton';
import { LifecycleActions } from '../shared/LifecycleActions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useSession } from '../../session';
import type { Receipt, RelSummary } from '../../types';
import { Badge, Dialog, Empty, ErrorBox, Loading, day, dt, inr, statusTone } from '../../ui';
import { Icon, rs } from '../../brand';
import { CustomerRows } from '../supplier/Dashboard';
import { InviteButton } from '../shared/Onboarding';
import { AccountFigures, AgreementHistory, HistorySection, PaySupplier, ReportPayment, StatementsSection, UnpaidSection, useTab, type Agreement } from '../shared/RelationshipParts';

export function BuyerSuppliers() {
  const { can } = useSession();
  const [showArchived, setShowArchived] = useState(false);
  const q = useQuery({ queryKey: ['buyer-dash'], queryFn: () => api.get<{ suppliers: RelSummary[] }>('/api/buyer/dashboard') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const list = q.data!.suppliers.filter((s) => showArchived || s.status !== 'CLOSED');
  // One supplier: go straight to its bills.
  if (!showArchived && list.length === 1 && q.data!.suppliers.length === 1) return <Navigate to={`/buyer/suppliers/${list[0].relationshipId}`} replace />;
  return (
    <div className="stack">
      <div className="row between">
        <h1>Bills</h1>
        {can('buyer.relationships.manage') ? <InviteButton direction="BUYER_INVITES_SUPPLIER" /> : null}
      </div>
      <p className="muted">Choose a supplier to see its bills and pay.</p>
      <label className="check"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived suppliers</label>
      {!list.length ? <Empty>No suppliers yet.</Empty> : <CustomerRows customers={list} hrefBase="/buyer/suppliers" side="buyer" />}
    </div>
  );
}

/** The amount to pay next, with the two ways to act on it. */
function BillDue({ s, onPaid, onDetails }: { s: RelSummary; onPaid?: () => void; onDetails: () => void }) {
  const late = BigInt(s.overduePaise) > 0n;
  const due = s.nextDueDate && BigInt(s.nextDueAmountPaise) > 0n;
  if (!late && !due) {
    return (
      <section className="bill-due calm" aria-label="Next payment">
        <div className="bd-label">Nothing to pay right now</div>
        <div className="bd-sub">Fuel filled this period goes on the next bill.</div>
      </section>
    );
  }
  return (
    <section className={`bill-due ${late ? 'late' : ''}`} aria-label="Next payment">
      <div className="bd-label">{late ? `Overdue · pay now to keep using credit` : `To pay · by ${day(s.nextDueDate)}`}</div>
      <div className="bd-amount">{inr(late ? s.overduePaise : s.nextDueAmountPaise)}</div>
      {late && due ? <div className="bd-sub">Next: {inr(s.nextDueAmountPaise)} by {day(s.nextDueDate)}</div> : null}
      <div className="bd-actions">
        {onPaid ? <button className="btn primary huge" onClick={onPaid}>I have paid</button> : null}
        <button className="btn huge bd-alt" onClick={onDetails}><Icon name="bills" size={20} />UPI / bank</button>
      </div>
    </section>
  );
}

export function BuyerSupplierDetail() {
  const { id } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['buyer-supplier', id], queryFn: () => api.get<{ summary: RelSummary; agreements: Agreement[] }>(`/api/buyer/suppliers/${id}`) });
  const dash = useQuery({ queryKey: ['buyer-dash'], queryFn: () => api.get<{ suppliers: RelSummary[] }>('/api/buyer/dashboard') });
  const [payDetails, setPayDetails] = useState(false);
  const canSee = can('buyer.statements.view');
  const tabs = [
    ...(canSee ? [{ id: 'bills', label: 'Monthly bills' }] : []),
    ...(can('buyer.payments.report') ? [{ id: 'payments', label: 'Payments' }] : []),
    ...(canSee ? [{ id: 'history', label: 'Full history' }] : []),
    { id: 'terms', label: 'Credit terms' },
  ];
  const { tab, setTab, bar } = useTab(tabs, tabs[0].id);
  // "I have paid" opens the Payments tab and takes the user to the form,
  // even when that tab is already open and the form is below the fold.
  const [jumpToPayment, setJumpToPayment] = useState<{ paise: string; n: number }>({ paise: '0', n: 0 });
  useEffect(() => {
    if (!jumpToPayment.n || tab !== 'payments') return;
    const form = document.getElementById('report-payment');
    form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    form?.querySelector('input')?.focus({ preventScroll: true });
    // Once per click; the form has already taken the amount (child effects run first).
    setJumpToPayment((j) => ({ ...j, n: 0 }));
  }, [jumpToPayment, tab]);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { summary: s, agreements } = q.data!;
  const refresh = () => void qc.invalidateQueries();
  const pendingTerms = agreements.find((a) => a.status === 'PROPOSED');
  const others = (dash.data?.suppliers.length ?? 1) > 1;
  return (
    <div className="stack">
      <div className="page-head">
        {others ? <Link className="back" to="/buyer/suppliers" aria-label="All suppliers"><Icon name="back" size={24} stroke={2.4} /></Link> : null}
        <h1 style={{ flex: 1 }}>Bills</h1>
        <span className="supplier-chip">{s.counterpartyName}</span>
      </div>
      {s.status === 'CLOSED' ? <p className="alert">Archived. Existing bills and payments remain available.</p> : null}
      <div className="row"><CallButton phone={s.counterpartyPhone} label="Call the pump" /></div>
      {s.status === 'PENDING_OPENING_BALANCE' ? <div className="alert info">The supplier is entering your earlier unpaid bills. Credit purchases start after that.</div> : null}
      {s.freeze.frozen ? <div className="alert bad">Credit stopped by the supplier{s.freeze.reason ? `: ${s.freeze.reason}` : ''}.</div> : null}
      {pendingTerms ? <button className="strip warn" onClick={() => setTab('terms')}><Icon name="alert" size={20} /><span>New credit terms from {s.counterpartyName} – please check</span><Icon name="next" size={18} stroke={2.4} /></button> : null}
      {s.terms ? <BillDue s={s} onPaid={can('buyer.payments.report') ? () => { setTab('payments'); setJumpToPayment((j) => ({ paise: BigInt(s.overduePaise) > 0n ? s.overduePaise : s.nextDueAmountPaise, n: j.n + 1 })); } : undefined} onDetails={() => setPayDetails(true)} /> : null}
      {BigInt(s.pendingReceiptsPaise) > 0n ? (
        <div className="strip info"><Icon name="clock" size={20} /><span><b>{rs(s.pendingReceiptsPaise)}</b> sent · waiting for {s.counterpartyName} to confirm</span></div>
      ) : null}
      <AccountFigures s={s} side="buyer" hideDue />
      {can('buyer.relationships.manage') ? <div className="row"><LifecycleActions path={`/api/relationships/${s.relationshipId}/lifecycle`} returnTo="/buyer/suppliers" /></div> : null}
      {s.terms ? bar : null}
      {s.terms && tab === 'bills' ? (
        <>
          <StatementsSection relationshipId={s.relationshipId} canIssue={false} notBilledPaise={s.ageing.NOT_BILLED} />
          <p className="muted">Invoices uploaded by the pump appear beside each bill. They are copies for your accounts and do not add another amount to pay.</p>
          <UnpaidSection relationshipId={s.relationshipId} />
        </>
      ) : null}
      {s.terms && tab === 'payments' ? (
        <>
          <div id="report-payment" style={{ scrollMarginTop: 80 }}><ReportPayment relationshipId={s.relationshipId} side="buyer" onDone={refresh} prefill={jumpToPayment} /></div>
          <SupplierPayments relationshipId={s.relationshipId} />
        </>
      ) : null}
      {s.terms && tab === 'history' ? <HistorySection relationshipId={s.relationshipId} side="buyer" /> : null}
      {!s.terms || tab === 'terms' ? (
        <>
          <AgreementHistory list={agreements} canAccept={s.status !== 'CLOSED' && can('buyer.terms.accept')} onChanged={refresh} />
          {BigInt(s.depositHeldPaise) > 0n ? <div className="card"><h2>Security deposit</h2><p>{inr(s.depositHeldPaise)} held by the supplier. It does not change your credit or bills.</p></div> : null}
        </>
      ) : null}
      <Dialog open={payDetails} title={`Pay ${s.counterpartyName}`} onClose={() => setPayDetails(false)}>
        <PaySupplier details={s.paymentDetails} supplierName={s.counterpartyName} />
      </Dialog>
    </div>
  );
}

function SupplierPayments({ relationshipId }: { relationshipId: string }) {
  const q = useQuery({ queryKey: ['payments'], queryFn: () => api.get<Receipt[]>('/api/payments') });
  const list = q.data?.filter((r) => r.relationshipId === relationshipId) ?? [];
  return (
    <div className="card">
      <h2>Payments you told them about</h2>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !list.length ? <Empty>No payments yet.</Empty> : null}
      <ul className="list">
        {list.map((r) => (
          <li key={r._id} className="row between">
            <div>
              <strong className="num">{inr(r.amountPaise)}</strong> · paid {day(r.paidOn)}
              <div><small className="muted">{r.reference} · {r.number}</small></div>
            </div>
            <Badge tone={statusTone(r.status)}>{r.status === 'PENDING_CONFIRMATION' ? 'Waiting for them to confirm' : r.status === 'CONFIRMED' ? 'Confirmed' : r.status.toLowerCase()}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BuyerPayments() {
  const q = useQuery({ queryKey: ['payments'], queryFn: () => api.get<Receipt[]>('/api/payments') });
  return (
    <div className="card">
      <h1>Payments to suppliers</h1>
      <p className="muted">To tell a supplier you paid, open Bills and tap “I have paid”.</p>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !q.data.length ? <Empty>No payments recorded yet.</Empty> : null}
      <div className="table-wrap">
        <table>
          <thead><tr><th>Paid on</th><th>Reference</th><th>Status</th><th className="num">Amount</th><th className="num">Applied to bills</th></tr></thead>
          <tbody>
            {q.data?.map((r) => (
              <tr key={r._id}>
                <td>{day(r.paidOn)}<div><small>{r.number} · reported {dt(r.reportedAt)}</small></div></td>
                <td className="mono">{r.reference}</td>
                <td>
                  <Badge tone={statusTone(r.status)}>{r.status === 'PENDING_CONFIRMATION' ? 'Payment pending confirmation' : r.status.toLowerCase()}</Badge>
                  {r.rejectedReason ? <div><small>{r.rejectedReason}</small></div> : null}
                  {r.reversalReason ? <div><small>Reversed: {r.reversalReason}</small></div> : null}
                </td>
                <td className="num">{inr(r.amountPaise)}</td>
                <td className="num">{inr(r.allocatedPaise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
